// Cryptic-game VARIANT config — the single seam that turns the one cryptic engine (GameCryptic)
// into two games: the standard hard-100 (/labs/cryptic) and the easier Times Quick Cryptic pool
// (/labs/cryptic-easy). Everything that differs between the two arms lives here; the engine reads
// this config and is otherwise identical. Adding a third cryptic regime = one more entry.

import { CrypticPuzzle } from './cryptic/cryptic.data'
import { CRYPTIC_PUZZLES } from './cryptic/cryptic.data'
import { CRYPTIC_EASY_PUZZLES, CRYPTIC_EASY_CONDITIONS } from './cryptic/cryptic-easy.data'
import { CRYPTIC_CONDITIONS } from './cryptic/cryptic.conditions'
import { CRYPTIC_PRACTICE } from './cryptic/practice.data'
import { SurveySchema, labsCrypticSurvey, labsCrypticEasySurvey } from '../../shared/labs/survey'

export type CrypticVariantKey = 'standard' | 'easy'

export type CrypticVariant = {
  key: CrypticVariantKey
  // data field on every saved trace — keeps the two arms as separate (aggregatable) domains
  traceDomain: 'cryptic' | 'cryptic-easy'
  puzzles: CrypticPuzzle[] // scored test + free-play pool
  practice: CrypticPuzzle[] // reserved warm-up pool (never scored)
  // pre-determined 10-clue test groups (24-game model); when set, the test allocates one whole
  // group at random instead of drawing one random clue per band. Undefined = band-draw.
  conditions?: string[][]
  // per-variant localStorage keys so completing one arm doesn't unlock/seen-mark the other
  testDoneKey: string
  seenKey: string
  // feedback survey (distinct surveyId per arm) + its BQ gameType tag
  survey: SurveySchema
  surveyGameType: string
  // value stamped into analytics payloads so the shared event names still split by arm
  analyticsVariant: CrypticVariantKey
  // copy that differs between the two arms
  consentHeading: string
  consentSub: string
  consentExample: { clue: string; answer: string }
  testIntroBody: string // the {TEST_LEN} clues intro lede (TEST_LEN is prepended by the engine)
}

export const STANDARD_CRYPTIC: CrypticVariant = {
  key: 'standard',
  traceDomain: 'cryptic',
  puzzles: CRYPTIC_PUZZLES,
  conditions: CRYPTIC_CONDITIONS, // 24-game model (migrated from per-band random draw; schemaVersion 3)
  practice: CRYPTIC_PRACTICE,
  testDoneKey: 'labs_test_complete_cryptic',
  seenKey: 'labsCrypticSeen',
  survey: labsCrypticSurvey,
  surveyGameType: 'labs-cryptic',
  analyticsVariant: 'standard',
  consentHeading: 'Crack the cryptic clue',
  consentSub: 'Each clue hides its answer in wordplay. The number in brackets is the answer’s length.',
  consentExample: { clue: 'Bird’s home base? (4)', answer: 'NEST' },
  testIntroBody: 'cryptic clues across the difficulty range.',
}

export const EASY_CRYPTIC: CrypticVariant = {
  key: 'easy',
  traceDomain: 'cryptic-easy',
  puzzles: CRYPTIC_EASY_PUZZLES,
  conditions: CRYPTIC_EASY_CONDITIONS, // 24-game model: pre-determined groups of 10, allocated at random
  practice: CRYPTIC_PRACTICE, // the 3 reserved practice clues are already gentle — shared across arms
  testDoneKey: 'labs_test_complete_cryptic_easy',
  seenKey: 'labsCrypticEasySeen',
  survey: labsCrypticEasySurvey,
  surveyGameType: 'labs-cryptic-easy',
  analyticsVariant: 'easy',
  consentHeading: 'New to cryptics? Start here',
  consentSub: 'Each clue hides its answer in wordplay. The number in brackets is the answer’s length.',
  consentExample: { clue: 'Bird’s home base? (4)', answer: 'NEST' },
  testIntroBody: 'gentler cryptic clues to learn the tricks.',
}
